import {
    BarcodeOutlined,
    BgColorsOutlined,
    BorderlessTableOutlined,
    DeleteOutlined,
    ExpandOutlined,
    FieldNumberOutlined,
    FileSearchOutlined,
    GiftOutlined,
    InboxOutlined,
    MessageOutlined,
    NumberOutlined,
    SendOutlined,
    TableOutlined,
    TagOutlined,
    TrophyOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import InfoItemWithCopy from 'components/UI/InfoItemWithCopy';
import { OfferLabel } from 'components/UI/ProductCard/atoms/Typography/OfferLabel/OfferLabel';
import Quality from 'components/UI/Quality';
import { QualityColorEnum } from 'enums/qualityColor.enum';
import { isNil } from 'lodash';
import { formatFullCurrency } from 'utils/number';
import * as S from './ProductCartCardDesktop.styles';
import { IProductCartCardProps } from './types';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);

const isValidQuality = (quality: string): quality is QualityColorEnum =>
  Object.values(QualityColorEnum).includes(quality as QualityColorEnum);

export default function ProductCartCardDesktop({
  image,
  productName,
  producer,
  quality,
  packaging,
  deliveryDayLabel,
  packagesQuantity,
  totalUnits,
  packageValue,
  consolidatedUnitValue,
  unitValue,
  totalValue,
  shippingCost,
  onRemove,
  unitPerPacking = 0,
  observation = '',
  message = '',
  dimension = '',
  gfpLineId,
  colors = '',
  productCode = '',
  barCode = '',
  gfpDetails,
  offerType,
  conceptName,
}: IProductCartCardProps) {
  const isGfp = Boolean(gfpLineId);

  const gfpObs =
    gfpDetails?.qualityObservation1 || gfpDetails?.qualityObservation2;
  const gfpObsText =
    gfpDetails?.qualityObservation1 && gfpDetails?.qualityObservation2
      ? `${gfpDetails.qualityObservation1}; ${gfpDetails.qualityObservation2}`
      : gfpObs;

  return (
    <S.CardContainer>
      <S.MainContent>
        <S.ImageSection>
          <S.ProductImage src={image} alt={productName} />
        </S.ImageSection>

        <S.DetailsSection>
          <S.ProductHeader>
            <S.NameConceptGroup>
              <S.ProductName>{productName}</S.ProductName>
              {conceptName && (
                <Tooltip title={conceptName}>
                  <S.ConceptName>{conceptName}</S.ConceptName>
                </Tooltip>
              )}
            </S.NameConceptGroup>
            {offerType != null && <OfferLabel offerType={offerType} />}
            <S.InfoGroup>
              <S.Label>Produtor:</S.Label>
              <S.ProducerValue>{producer}</S.ProducerValue>
            </S.InfoGroup>
          </S.ProductHeader>

          <S.AttributesGrid>
            <S.InfoGroup>
              <S.Label>
                <TrophyOutlined />
                Qualidade:
              </S.Label>
              <S.QualityWrapper>
                {isValidQuality(quality) ? (
                  <Quality quality={quality} />
                ) : (
                  <S.Value>{quality}</S.Value>
                )}
              </S.QualityWrapper>
            </S.InfoGroup>

            <S.InfoGroup>
              <S.Label>
                <GiftOutlined />
                Embalagem:
              </S.Label>
              <S.Value>{packaging}</S.Value>
            </S.InfoGroup>

            <S.InfoGroup>
              <S.Label>
                <BgColorsOutlined />
                Cores:
              </S.Label>
              <S.Value>{colors || 'N/A'}</S.Value>
            </S.InfoGroup>

            <S.InfoGroup>
              <S.Label>
                <ExpandOutlined />
                Dimensão/Altura:
              </S.Label>
              <S.Value>{dimension || 'N/A'}</S.Value>
            </S.InfoGroup>

            <InfoItemWithCopy
              label={
                <S.Label>
                  <TagOutlined />
                  Cód. do Produto
                </S.Label>
              }
              value={productCode}
              fallback="Não informado"
              removeColon
            />

            <InfoItemWithCopy
              label={
                <S.Label>
                  <BarcodeOutlined />
                  Cód. de Barras
                </S.Label>
              }
              value={barCode}
              fallback="Não informado"
              removeColon
            />

            <S.InfoGroup>
              <S.Label>
                <MessageOutlined />
                Observação da Oferta:
              </S.Label>
              <S.Value>{message ?? '-'}</S.Value>
            </S.InfoGroup>
          </S.AttributesGrid>

          {isGfp && !isNil(gfpDetails) && (
            <>
              <S.AttributesGrid>
                <S.SectionTitle>Sobre a GFP</S.SectionTitle>
              </S.AttributesGrid>
              <S.AttributesGrid>
                <S.InfoGroup>
                  <S.Label>
                    <SendOutlined />
                    Entrega CVH:
                  </S.Label>
                  <S.Value>{gfpDetails.deliveryDate}</S.Value>
                </S.InfoGroup>

                <S.InfoGroup>
                  <S.Label>
                    <BorderlessTableOutlined />
                    Série:
                  </S.Label>
                  <S.Value>{gfpDetails.series || 'Não informado'}</S.Value>
                </S.InfoGroup>
                <S.InfoGroup>
                  <S.Label>
                    <FieldNumberOutlined />
                    Número da GFP:
                  </S.Label>
                  <S.Value>{gfpDetails.gfpNumber || 'Não informado'}</S.Value>
                </S.InfoGroup>
                <S.InfoGroup>
                  <S.Label>
                    <TableOutlined />
                    Lote:
                  </S.Label>
                  <S.Value>{gfpDetails.lot || 'Não informado'}</S.Value>
                </S.InfoGroup>

                <S.InfoGroup>
                  <S.Label>
                    <FileSearchOutlined />
                    Observações da GFP:
                  </S.Label>
                  <S.Value>{gfpObsText ?? '-'}</S.Value>
                </S.InfoGroup>

                <S.InfoGroup>
                  <S.Label>
                    <MessageOutlined />
                    Observações do Produtor:
                  </S.Label>
                  <S.Value>{gfpDetails.observation ?? '-'}</S.Value>
                </S.InfoGroup>
              </S.AttributesGrid>
            </>
          )}

          <S.DeliveryInfo>
            <S.InfoGroup>
              <S.Label>
                <InboxOutlined />
                Quantidade de Embalagem:
              </S.Label>
              <S.Value>{packagesQuantity}</S.Value>
            </S.InfoGroup>

            <S.InfoGroup>
              <S.Label>
                <NumberOutlined />
                Total de Unidades:
              </S.Label>
              <S.Value>{totalUnits}</S.Value>
            </S.InfoGroup>
          </S.DeliveryInfo>
        </S.DetailsSection>

        <S.PriceSection>
          <S.PriceColumn>
            <S.PriceRow>
              <S.Label>Valor Embalagem:</S.Label>
              <S.Price>{formatCurrency(packageValue)}</S.Price>
            </S.PriceRow>

            <S.PriceRow>
              <S.Label>Valor Unit. Consolidado:</S.Label>
              <S.Price>{formatCurrency(consolidatedUnitValue)}</S.Price>
            </S.PriceRow>

            <S.PriceRow>
              <S.Label>Valor Unitário:</S.Label>
              <S.Price>{formatCurrency(unitValue)}</S.Price>
            </S.PriceRow>

            <S.TotalPriceRow>
              <S.Label>Valor Total:</S.Label>
              <S.TotalPrice>{formatCurrency(totalValue)}</S.TotalPrice>
            </S.TotalPriceRow>
          </S.PriceColumn>

          <S.ActionsSection>
            <S.FreightInfo>
              <S.FreightPrice>
                Frete: {formatFullCurrency(shippingCost)}
              </S.FreightPrice>
            </S.FreightInfo>

            <S.RemoveButton icon={<DeleteOutlined />} onClick={onRemove}>
              Remover
            </S.RemoveButton>
          </S.ActionsSection>
        </S.PriceSection>
      </S.MainContent>
    </S.CardContainer>
  );
}
